import { useState, forwardRef, useImperativeHandle } from "react";
import { Trans } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { CommonButton } from "@components/CommonButton";
import { BasicInfoContent } from "./BasicInfoContent";
import {
  setBasicInfo,
  setProductInfo,
  selectVideoCreatingParams,
  selectStepStatus,
  setStepStatus,
} from "../videoCreatingSlice";
import type { ProductImage } from "../videoCreatingSlice";
import message from "@utils/message";
import report from "@report";
import { useSerializeCallback } from "@kalodata/tools";
import { fetchAiAnalyzeImage } from "@apis/videoCreatingService";
import { useNeedAiAnalyzeImages } from "./useNeedAiAnalyzeImages";
import { DEFAULT_VIDEO_LANGUAGE } from "@utils/util";
import { useCreateVideoOptions } from "@hooks/useCreateVideoOptions";
import { TestId } from "@shared/testIds";
import { CreditGuardButton } from "@components/CreditGuardButton";

export interface BasicInfoRef {
  triggerNext: () => Promise<void>;
}

interface BasicInfoProps {
  onNext?: () => void;
  onParseProductFailed: () => void;
}

export const BasicInfo = forwardRef<BasicInfoRef, BasicInfoProps>((props, ref) => {
  const { onNext, onParseProductFailed } = props;
  const dispatch = useAppDispatch();
  const stepStatus = useAppSelector(selectStepStatus);
  const params = useAppSelector(selectVideoCreatingParams);
  const { basicInfo, country, productId, productInfo } = params;
  const { productTitle, productCategory, images } = basicInfo;
  const needAiAnalyzeImages = useNeedAiAnalyzeImages(images);
  const videoLanguage = productInfo.language || DEFAULT_VIDEO_LANGUAGE;

  useCreateVideoOptions(videoLanguage);

  const [imageUploading, setImageUploading] = useState(false);
  const canProceed = images.length > 0 && !!productTitle.trim() && !!productCategory.trim();

  const handleNext = useSerializeCallback(async () => {
    if (!canProceed || needAiAnalyzeImages.length > 0) return;
    try {
      report.click({
        kalo_type: "click_next_step1",
      });
      onNext?.();
    } catch (error: any) {
      message.error(error?.message || "Failed to upload images");
    }
  });

  const handleAiAnalyze = useSerializeCallback(async () => {
    if (!canProceed || needAiAnalyzeImages.length === 0) return;
    const results = await fetchAiAnalyzeImage({
      title: productTitle,
      category: productCategory,
      imageUrls: needAiAnalyzeImages.map((image) => image.url),
    });
    const successImages = results.filter((result) => result.success);
    const nextImages = images
      .map<ProductImage | null>((e) => {
        const sendAiAnalyzeImage = needAiAnalyzeImages.find((image) => image.url === e.url);
        if (sendAiAnalyzeImage) {
          const aiAnalyzed = successImages.find((successImage) => successImage.imageUrl === e.url);
          const description = (aiAnalyzed?.description ?? e.description) || "";
          if (!aiAnalyzed) return null;
          return {
            ...e,
            description,
            aiAnalyzed: true,
          } satisfies ProductImage;
        }
        return e;
      })
      .filter((image): image is ProductImage => image !== null);
    report.click({
      kalo_type: "click_ai_analyze",
    });
    dispatch(setBasicInfo({ images: nextImages }));
  });

  const loading = handleNext.isPending() || handleAiAnalyze.isPending();

  useImperativeHandle(ref, () => ({ triggerNext: handleNext }), [handleNext]);

  return (
    <div className="BasicInfo flex flex-col h-[calc(100vh-125px)] bg-kds-bg relative">
      <div className="flex-1 overflow-y-auto p-[24px]">
        <BasicInfoContent
          isAIAnalyzing={handleAiAnalyze.isPending()}
          images={images}
          onImagesChange={(nextImages) => {
            dispatch(setBasicInfo({ images: nextImages }));
            dispatch(setStepStatus({ basicInfoInit: true } as any));
          }}
          productTitle={productTitle}
          productId={stepStatus.basicInfoInit ? undefined : productId}
          country={country}
          onUploadingChange={setImageUploading}
          productCategory={productCategory}
          onChangeBasicInfo={(e) => {
            dispatch(setBasicInfo(e));
            if ("productCategory" in e) {
              dispatch(setProductInfo({ category: e.productCategory }));
            }
          }}
          onParseProductFailed={onParseProductFailed}
        />
      </div>

      {/* Footer */}
      <div className="bg-kds-bg border-t border-kds-border p-4 flex justify-end">
        {needAiAnalyzeImages.length > 0 ? (
          <CommonButton
            size="large"
            type="primary"
            className="min-w-[96px]"
            disabled={!canProceed || loading}
            loading={loading || imageUploading}
            onClick={handleAiAnalyze}
          >
            <Trans>AI Analyze</Trans>
          </CommonButton>
        ) : (
          <CreditGuardButton
            size="large"
            type="primary"
            className="min-w-[96px]"
            data-testid={TestId.VideoBasicNextButton}
            disabled={!canProceed || loading}
            loading={loading || imageUploading}
            onClick={handleNext}
          >
            <Trans>Next</Trans>
          </CreditGuardButton>
        )}
      </div>
    </div>
  );
});

BasicInfo.displayName = "BasicInfo";
